import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import VisitsService from '../../../services/VisitsService'
import PatientChartsService from '../../../services/PatientChartsService'
import FileManagerService from '../../../services/FileManagerService'
import EmployeeService from '../../../services/EmployeeService'
import ActivityCharts from '../../EmployeeVisitContainer/EmployeeVisitDetailView/RightContent/ActivityChart'
import Pagination from '../../../components/UI/TableGrid/Pagination'
import SortableTh from './SortableTh'

const ReviewCompletedOrdersFullscreen = ({ isOpen, onClose, initialPosition, dateFilter = 'year', filters = {}, Loader, Toast, navigate, stateList = [] }) => {
  const [tableData, setTableData] = useState([])
  const [allData, setAllData] = useState([])
  const [loading, setLoading] = useState(false)
  const [isModelOpen, setIsModelOpen] = useState(false)
  const [activeEditId, setActiveEditId] = useState(null)
  const [activeVisitId, setActiveVisitId] = useState(null)
  const [activityChartDetails, setActivityChartDetails] = useState({})
  const [visitDetails, setVisitDetails] = useState(null)
  const [employeeDetails, setEmployeeDetails] = useState(null)
  const [chartModalLoading, setChartModalLoading] = useState(false)
  const [positionState, setPositionState] = useState(null)
  const originalPositionRef = useRef(null)
  const hasExpandedRef = useRef(false)
  const [pagination, setPagination] = useState({ page: 1, perPage: 10, totalRecords: 0 })
  const [sortColumn, setSortColumn] = useState('visit_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (isOpen) fetchData()
  }, [isOpen, dateFilter, filters, sortColumn, sortDirection])

  useEffect(() => {
    if (isOpen && initialPosition) {
      hasExpandedRef.current = false
      originalPositionRef.current = initialPosition
      setPositionState(initialPosition)
      const t = setTimeout(() => {
        setPositionState(null)
        hasExpandedRef.current = true
      }, 50)
      return () => clearTimeout(t)
    }
    if (!isOpen) {
      setPositionState(null)
      originalPositionRef.current = null
      hasExpandedRef.current = false
    }
  }, [isOpen, initialPosition])

  const fetchData = async (searchValue = null) => {
    setLoading(true)
    try {
      const currentSearchTerm = searchValue !== null ? searchValue : searchTerm
      const payload = {
        date_filter: dateFilter || 'year',
        sort_by: sortColumn,
        sort_order: sortDirection,
        ...filters,
        ...(currentSearchTerm && currentSearchTerm.trim() ? { patient_name: currentSearchTerm.trim() } : {})
      }
      const response = await VisitsService.getReviewCompletedOrders(payload)
      if (response?.review_completed_orders) {
        const list = response.review_completed_orders
        setAllData(list)
        setPagination(prev => ({ ...prev, totalRecords: list.length, page: 1 }))
        setTableData(list.slice(0, pagination.perPage))
      } else {
        setAllData([])
        setTableData([])
      }
    } catch (e) {
      console.error(e)
      setAllData([])
      setTableData([])
    } finally {
      setLoading(false)
    }
  }

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination)
  }

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(col)
      setSortDirection('asc')
    }
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  useEffect(() => {
    if (allData.length > 0) {
      const start = (pagination.page - 1) * pagination.perPage
      setTableData(allData.slice(start, start + pagination.perPage))
    }
  }, [pagination.page, pagination.perPage, allData])

  const printlabel = async (row, type, labelName = 'lab') => {
    try {
      const queryfilters = { label_type: type, visit_id: row.visit_id }
      let data
      if (type === 'lab_report') data = await PatientChartsService.getLaboratoryReport(null, queryfilters, row.id)
      else if (type === 'lab_requisition') data = await PatientChartsService.getLabRequisition(null, queryfilters, row.id)
      else data = await PatientChartsService.getPrintLabel(null, queryfilters, row.id)
      FileManagerService.printPdf({ file_content: data, original_name: labelName, mime_type: 'application/pdf' })
    } catch (e) {
      console.error(e)
    }
  }

  const editHandler = async (row) => {
    if (!row.mapping_id) {
      Toast?.error?.('Chart not available for this order.')
      return
    }
    setActiveVisitId(row.visit_id)
    setActiveEditId(row.id)
    setChartModalLoading(true)
    Loader && Loader(true)
    try {
      const visitData = await VisitsService.getVisits(row.visit_id)
      const visit = visitData?.[0]
      if (visit) {
        setVisitDetails(visit)
        if (visit.employee_id) {
          try {
            const empData = await EmployeeService.getEmployee(visit.employee_id)
            setEmployeeDetails(empData)
          } catch (e) {
            setEmployeeDetails(visit.employee || null)
          }
        } else {
          setEmployeeDetails(visit.employee || null)
        }
        setActivityChartDetails({
          mapping_id: row.mapping_id,
          activity_name: row.activity_name || 'Lab',
          activity_type: row.activity_type || 'Lab',
          actitivity_id: row.actitivity_id
        })
        setIsModelOpen(true)
      } else {
        Toast?.error?.('Visit not found')
        setIsModelOpen(false)
        Loader && Loader(false)
      }
    } catch (error) {
      console.error('Error fetching visit details:', error)
      Toast?.error?.('Failed to load chart')
      setIsModelOpen(false)
      Loader && Loader(false)
    } finally {
      setChartModalLoading(false)
      Loader && Loader(false)
    }
  }

  const addFormHandler = () => {
    setIsModelOpen(false)
    setActiveEditId(null)
    setActiveVisitId(null)
    setVisitDetails(null)
    setEmployeeDetails(null)
    setActivityChartDetails({})
  }

  const getStatusBubbleMeta = (status) => {
    const statusNorm = String(status || '').toLowerCase()
    const isSuccess = ['passed', 'normal', 'negative', 'confirmed negative', 'administrated', 'administered', 'dispensed', 'completed', 'complete'].includes(statusNorm)
    const isPending = ['not started', 'not administered'].includes(statusNorm)
    const isDanger = ['failed', 'cancelled', 'abnormal', 'positive', 'confirmed positive'].includes(statusNorm)

    if (isSuccess) return { badgeClass: 'sh_badgeSuccess', dotColor: '#0F8647' }
    if (isPending) return { badgeClass: 'sh_badgePending', dotColor: '#1D2433CC' }
    if (isDanger) return { badgeClass: 'sh_badgeDanger', dotColor: '#E02D3C' }
    return { badgeClass: 'sh_badgeWarning', dotColor: '#CA8B14' }
  }

  const renderStatusBubble = (status) => {
    const { badgeClass, dotColor } = getStatusBubbleMeta(status)
    return (
      <div className={`badge ${badgeClass}`}>
        <span className="pr-1">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="4" cy="4" rx="4" ry="4" fill={dotColor} />
          </svg>
        </span>
        {status || '-'}
      </div>
    )
  }

  const handleClose = () => {
    if (originalPositionRef.current) {
      setPositionState(originalPositionRef.current)
      setTimeout(() => onClose(), 300)
    } else onClose()
  }

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4L4 12M4 4L12 12" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
  const ViewIcon = () => (
    <svg width="18" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.7683 8.59714C12.7442 9.57297 12.7442 11.1571 11.7683 12.1346C10.7925 13.1105 9.20834 13.1105 8.23084 12.1346C7.255 11.1588 7.255 9.57464 8.23084 8.59714C9.20834 7.61964 10.7917 7.61964 11.7683 8.59714" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path fillRule="evenodd" clipRule="evenodd" d="M2.5 10.3639C2.5 9.81468 2.62667 9.27135 2.87167 8.77052V8.77052C4.13417 6.18968 6.92417 4.53052 10 4.53052C13.0758 4.53052 15.8658 6.18968 17.1283 8.77052V8.77052C17.3733 9.27135 17.5 9.81468 17.5 10.3639C17.5 10.913 17.3733 11.4564 17.1283 11.9572V11.9572C15.8658 14.538 13.0758 16.1972 10 16.1972C6.92417 16.1972 4.13417 14.538 2.87167 11.9572V11.9572C2.62667 11.4564 2.5 10.913 2.5 10.3639Z" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
  const EditIcon = () => (
     <svg width="18" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.83203 6.66667V3.33333C5.83203 2.87333 6.20536 2.5 6.66536 2.5H13.332C13.792 2.5 14.1654 2.87333 14.1654 3.33333V6.66667"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.83333 14.1667H4.16667C3.24583 14.1667 2.5 13.4209 2.5 12.5001V8.33341C2.5 7.41258 3.24583 6.66675 4.16667 6.66675H15.8333C16.7542 6.66675 17.5 7.41258 17.5 8.33341V12.5001C17.5 13.4209 16.7542 14.1667 15.8333 14.1667H14.1667"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.83203 11.5H14.1654V16.6667C14.1654 17.1267 13.792 17.5 13.332 17.5H6.66536C6.20536 17.5 5.83203 17.1267 5.83203 16.6667V11.5Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M5.83203 9.16667H6.66536" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )

  if (!isOpen) return null

  const effectivePosition = (isOpen && initialPosition && positionState === null && !hasExpandedRef.current)
    ? initialPosition
    : (positionState != null ? positionState : null)

  return (
    <>
      {isModelOpen && chartModalLoading ? (
        <div className="position-fixed top-0 left-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <span className="text-white">Loading chart...</span>
        </div>
      ) : null}
      {isModelOpen && visitDetails && Object.keys(activityChartDetails).length > 0 && !chartModalLoading ? (
        <ActivityCharts
          chartDetails={activityChartDetails}
          isOpen={isModelOpen}
          visitDetails={visitDetails}
          fetchVisitDetails={fetchData}
          stateList={stateList}
          employeeDetails={employeeDetails || visitDetails?.employee || {}}
          lookupsOptions={{}}
          companyDetails={visitDetails?.company}
          toogleHanlder={addFormHandler}
          Toast={Toast}
          Loader={Loader}
          employeeId={visitDetails?.employee_id || visitDetails?.employee?.employee_id}
          fetchEmployeeDetails={async (empId) => {
            try {
              const data = await EmployeeService.getEmployee(empId)
              setEmployeeDetails(data)
            } catch (e) {
              console.error(e)
            }
          }}
        />
      ) : null}
      <div
        className="tblviewcard flip fullscreen-card"
        style={{
          position: 'fixed',
          top: effectivePosition ? `${effectivePosition.top}px` : 0,
          left: effectivePosition ? `${effectivePosition.left}px` : 0,
          width: effectivePosition ? `${effectivePosition.width}px` : '100vw',
          height: effectivePosition ? `${effectivePosition.height}px` : '100vh',
          zIndex: 1040,
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: effectivePosition ? '8px' : 0,
          boxShadow: effectivePosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <div className="card mb-0 border-0 shadow-none" style={{ height: '100%', margin: 0 }}>
          <div className="card-header sh_accordionheader">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="text-md font-weight-bold mb-0">Review Completed Orders</h6>
              <a className="btn sh_btniconnew" style={{ cursor: 'pointer' }} onClick={handleClose} title="Minimize">
                <CloseIcon />
              </a>
            </div>
          </div>
          <div className="card-body px-0 pt-0 mt-2 pb-2" style={{ height: 'calc(100% - 60px)', overflow: 'auto' }}>
            <div className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-end" style={{ borderBottom: '1px solid #e0e0e0' }}>
              <div className="d-flex align-items-center">
                <div className="mr-2">
                  <input
                    type="text"
                    placeholder="Patient"
                    value={searchTerm}
                    className="form-control"
                    style={{ minWidth: '200px' }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const trimmed = searchTerm.trim()
                        setSearchTerm(trimmed)
                        fetchData(trimmed)
                      }
                    }}
                  />
                </div>
                <div className="addAction">
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        const trimmed = searchTerm.trim()
                        setSearchTerm(trimmed)
                        fetchData(trimmed)
                      }}
                    >
                      Search
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setSearchTerm('')
                        setPagination(prev => ({ ...prev, page: 1 }))
                        fetchData('')
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div>
              </div>
            ) : (
              <>
                <table className="table dataTable sh_tablenew nowrap table-hover" width="100%" cellSpacing="0" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <SortableTh label="PROVIDER" sortKey="provider" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTh label="PATIENT" sortKey="patient" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    {/* <SortableTh label="TYPE" sortKey="type" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /> */}
                    <SortableTh label="DATE" sortKey="visit_date" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTh label="ORDER PLACED" sortKey="order_name" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTh label="RESULT" sortKey="status" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTh label="STATUS" sortKey="activity_status" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTh label="ACTION" sortable={false} />
                  </tr>
                </thead>
                  <tbody>
                    {tableData.length === 0 ? (
                      <tr><td colSpan="7" className="text-center py-3">No orders found</td></tr>
                    ) : (
                      tableData.map((row, index) => (
                        <tr key={row.id || index}>
                          <td className="forTable_textEllipsis">{row.provider}</td>
                          <td className="forTable_textEllipsis">{row.patient}</td>
                          {/* <td className="forTable_textEllipsis">{row.type}</td> */}
                          <td className="forTable_textEllipsis">{row.visit_date}</td>
                          <td className="forTable_textEllipsis">{row.order_name}</td>
                          <td className="forTable_textEllipsis">{renderStatusBubble(row.status)}</td>
                          <td className="forTable_textEllipsis">{renderStatusBubble(row.activity_status)}</td>
                          <td className="forTable_textEllipsis">
                            <a className="btn btn-icon px-1 btn-transparent-dark" title={row.review_order_kind === 'xray' ? 'View X-Ray' : 'View Lab'} style={{ cursor: 'pointer' }} onClick={() => editHandler(row)}>
                              <ViewIcon />
                            </a>
                            {row.review_order_kind !== 'xray' ? (
                            <a className="btn btn-icon px-1 btn-transparent-dark" title="Print Lab Report" style={{ cursor: 'pointer' }} onClick={() => printlabel(row, 'lab_report')}>
                              <EditIcon />
                            </a>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {allData.length > 0 && pagination.totalRecords > pagination.perPage && (
                  <div className="px-3 pb-2">
                    <Pagination pagination={pagination} fetchTableRecords={handlePaginationChange} tableRows={{ data: tableData }} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const mapStateToProps = (state) => ({
  stateList: state.session?.profileStateList || []
})

export default connect(mapStateToProps)(ReviewCompletedOrdersFullscreen)
